/**
 * FullVSL YouTube IFrame API backend (FUL-243).
 *
 * Wraps the official YouTube IFrame Player API to expose a surface compatible
 * with the FullVSL player framework: custom controls, progress polling, event
 * mapping to the PlayerEvent vocabulary, and resume-seek support.
 *
 * Key constraints (PRD §7.2, common.md):
 *  - Do NOT promise or attempt YouTube branding removal.
 *  - Overlays render OUTSIDE the YouTube iframe (positioned by the FullVSL
 *    player layout), so the backend only owns the iframe region.
 *  - YouTube native controls are hidden (playerVars.controls=0) because we
 *    render our own; the YouTube logo is unaffected by this setting.
 *  - No autoplay guarantee — the IFrame API respects browser autoplay policy
 *    like the HTML5 player.
 */

import type { PlayerMediaLike } from "./player-media";

/* =====================================================================================
 * Pure helpers (no DOM — unit-tested)
 * ===================================================================================== */

/**
 * YouTube IFrame API player states (YT.PlayerState values).
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
export const YTState = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

export type YtPlayerState = (typeof YTState)[keyof typeof YTState];

/**
 * Extract the 11-character video ID from any common YouTube URL format:
 *  - https://www.youtube.com/watch?v=ID
 *  - https://youtu.be/ID
 *  - https://www.youtube.com/embed/ID
 *  - https://www.youtube.com/shorts/ID
 *  - https://m.youtube.com/watch?v=ID
 *  - https://www.youtube.com/live/ID
 *
 * Returns null when no valid 11-char ID is found. Also accepts a bare video ID.
 */
export function parseYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();

  // Bare 11-char video ID.
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, "").replace(/^m\./, "");

    // youtu.be/ID
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      return isValidId(id) ? id : null;
    }

    // youtube.com/watch?v=ID
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      const v = u.searchParams.get("v");
      if (v && isValidId(v)) return v;

      const parts = u.pathname.split("/").filter(Boolean);
      // /embed/ID, /shorts/ID, /live/ID, /v/ID
      if (parts.length >= 2 && ["embed", "shorts", "live", "v"].includes(parts[0])) {
        return isValidId(parts[1]) ? parts[1] : null;
      }
    }
    return null;
  } catch {
    // Not a valid URL — try a fallback regex for "v=ID" query-style strings.
    const match = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  }
}

function isValidId(id: string | null | undefined): boolean {
  return !!id && /^[a-zA-Z0-9_-]{11}$/.test(id);
}

/**
 * Map a YT player state to a coarse FullVSL media state descriptor.
 * Returns the normalized state name the YouTubePlayer event handlers consume.
 */
export function mapYtState(state: YtPlayerState): {
  playing: boolean;
  paused: boolean;
  ended: boolean;
  buffering: boolean;
} {
  switch (state) {
    case YTState.PLAYING:
      return { playing: true, paused: false, ended: false, buffering: false };
    case YTState.PAUSED:
      return { playing: false, paused: true, ended: false, buffering: false };
    case YTState.ENDED:
      return { playing: false, paused: false, ended: true, buffering: false };
    case YTState.BUFFERING:
      return { playing: false, paused: false, ended: false, buffering: true };
    default:
      // UNSTARTED or CUED
      return { playing: false, paused: true, ended: false, buffering: false };
  }
}

/* =====================================================================================
 * Minimal YT namespace declarations (no @types/youtube dependency)
 * ===================================================================================== */

export interface YtPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(volume: number): void;
  getVolume(): number;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  getVideoLoadedFraction(): number;
  getPlaybackRate(): number;
  setPlaybackRate(rate: number): void;
  destroy(): void;
}

interface YtPlayerEvent {
  target: YtPlayer;
  data: number;
}

interface YtPlayerCreateOptions {
  width?: string | number;
  height?: string | number;
  videoId?: string;
  playerVars?: Record<string, unknown>;
  events?: {
    onReady?: (event: YtPlayerEvent) => void;
    onStateChange?: (event: YtPlayerEvent) => void;
    onPlaybackQualityChange?: (event: YtPlayerEvent) => void;
    onPlaybackRateChange?: (event: YtPlayerEvent) => void;
    onError?: (event: YtPlayerEvent) => void;
  };
}

interface YtNamespace {
  Player: new (el: HTMLElement | string, options: YtPlayerCreateOptions) => YtPlayer;
  PlayerState: typeof YTState;
}

/* =====================================================================================
 * YouTubeBackend
 * ===================================================================================== */

/** Callbacks the YouTubePlayer wires to drive event dispatching. */
export interface YouTubeBackendCallbacks {
  /** Fires when the YT.Player is ready and duration/currentTime are available. */
  onReady?: () => void;
  /** Fires when playback starts (state → PLAYING). */
  onPlay?: () => void;
  /** Fires when playback pauses (state → PAUSED). */
  onPause?: () => void;
  /** Fires when the video ends (state → ENDED). */
  onEnded?: () => void;
  /**
   * Periodic progress poll (every POLL_INTERVAL_MS while playing).
   * Supplies the current playback position in seconds.
   */
  onTimeUpdate?: (currentTimeSec: number) => void;
  /** Fires when the buffered fraction changes meaningfully. */
  onBufferedFraction?: (fraction: number) => void;
  /** Fires on YT API error (bad video ID, embedding disabled, etc.). */
  onError?: (code: number, message: string) => void;
}

/** Polling interval for progress tracking (ms). YouTube has no timeupdate event. */
const POLL_INTERVAL_MS = 250;

/** YT IFrame API error codes → messages (from the official docs). */
const YT_ERROR_MESSAGES: Record<number, string> = {
  2: "Invalid video ID parameter",
  5: "HTML5 player error",
  100: "Vídeo não encontrado ou privado",
  101: "O proprietário não permite a reprodução incorporada",
  150: "O proprietário não permite a reprodução incorporada",
};

/**
 * YouTube IFrame API wrapper. Loads the API script (once), creates a YT.Player
 * inside a host element, and exposes a PlayerMediaLike interface so the shared
 * CtaController / ResumeWiring controllers work identically to HTML5 mode.
 *
 * The backend also drives a polling loop for progress tracking (YouTube has no
 * timeupdate event) and fires normalized callbacks the YouTubePlayer maps to
 * PlayerEvent vocabulary.
 */
export class YouTubeBackend implements PlayerMediaLike {
  private readonly hostEl: HTMLElement;
  private readonly videoId: string;
  private readonly startMuted: boolean;
  private readonly callbacks: YouTubeBackendCallbacks;
  private player: YtPlayer | null = null;
  private ready = false;
  private destroyed = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private _paused = true;
  private _ended = false;
  private _muted: boolean;
  private _currentTime = 0;
  private _duration = NaN;
  private _bufferedFraction = 0;
  private seekingFlag = false;

  constructor(opts: {
    hostEl: HTMLElement;
    videoId: string;
    muted?: boolean;
    callbacks?: YouTubeBackendCallbacks;
  }) {
    this.hostEl = opts.hostEl;
    this.videoId = opts.videoId;
    this.startMuted = opts.muted ?? false;
    this._muted = this.startMuted;
    this.callbacks = opts.callbacks ?? {};
    void this.init();
  }

  /**
   * Load the YT IFrame API script (idempotent) and create the player once the
   * global `YT` namespace + `onYouTubeIframeAPIReady` callback resolves.
   */
  private async init(): Promise<void> {
    try {
      await this.loadApiScript();
      if (this.destroyed) return;
      this.createPlayer();
    } catch {
      this.callbacks.onError?.(0, "Não foi possível carregar a API de incorporação do YouTube");
    }
  }

  /** Inject the YouTube IFrame API <script> (deduped across instances). */
  private loadApiScript(): Promise<void> {
    return new Promise((resolve) => {
      const w = window as unknown as {
        YT?: YtNamespace;
        onYouTubeIframeAPIReady?: () => void;
      };

      // Already loaded.
      if (w.YT && w.YT.Player) {
        resolve();
        return;
      }

      // Script already injected but not ready yet — hook the callback.
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src="https://www.youtube.com/iframe_api"]',
      );
      if (existing) {
        const prev = w.onYouTubeIframeAPIReady;
        w.onYouTubeIframeAPIReady = () => {
          prev?.();
          resolve();
        };
        return;
      }

      // Fresh injection.
      const prevCb = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => {
        prevCb?.();
        resolve();
      };
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);
    });
  }

  /** Create the YT.Player instance inside the host element. */
  private createPlayer(): void {
    const w = window as unknown as { YT?: YtNamespace };
    if (!w.YT || !w.YT.Player) {
      this.callbacks.onError?.(0, "YouTube IFrame API not available");
      return;
    }

    this.player = new w.YT.Player(this.hostEl, {
      videoId: this.videoId,
      width: "100%",
      height: "100%",
      playerVars: {
        controls: 0, // hide native controls — FullVSL renders its own
        rel: 0, // don't show related videos at the end
        modestbranding: 1, // deprecated but harmless
        iv_load_policy: 3, // hide annotations
        playsinline: 1, // iOS: don't force fullscreen
        fs: 0, // hide YT fullscreen button
        disablekb: 1, // disable YT keyboard shortcuts (we handle our own)
        origin: typeof location !== "undefined" ? location.origin : undefined,
      },
      events: {
        onReady: () => this.onPlayerReady(),
        onStateChange: (e: YtPlayerEvent) => this.onStateChange(e.data),
        onError: (e: YtPlayerEvent) => this.onPlayerError(e.data),
      },
    });
  }

  private onPlayerReady(): void {
    if (this.destroyed || !this.player) return;
    this.ready = true;
    this._duration = this.player.getDuration() || NaN;

    // Apply initial mute state.
    if (this._muted) {
      this.player.mute();
    } else {
      this.player.unMute();
    }

    this.callbacks.onReady?.();
  }

  private onStateChange(state: number): void {
    if (this.destroyed) return;
    const mapped = mapYtState(state as YtPlayerState);
    const wasPlaying = !this._paused && !this._ended;

    this._paused = mapped.paused;
    this._ended = mapped.ended;

    // Update duration on first playing/buffering (not available at onReady for
    // some videos).
    if (this.player && !Number.isFinite(this._duration)) {
      this._duration = this.player.getDuration() || NaN;
    }

    if (mapped.playing) {
      this.startPolling();
      this.callbacks.onPlay?.();
    } else if (mapped.paused) {
      this.stopPolling();
      if (wasPlaying) {
        // Only fire pause when transitioning from playing.
        this.callbacks.onPause?.();
      }
    } else if (mapped.ended) {
      this.stopPolling();
      this.callbacks.onEnded?.();
    }

    // Update buffered fraction on state changes.
    if (this.player) {
      const frac = this.player.getVideoLoadedFraction();
      if (frac !== this._bufferedFraction) {
        this._bufferedFraction = frac;
        this.callbacks.onBufferedFraction?.(frac);
      }
    }
  }

  private onPlayerError(code: number): void {
    if (this.destroyed) return;
    const message = YT_ERROR_MESSAGES[code] ?? `YouTube error ${code}`;
    this.callbacks.onError?.(code, message);
  }

  /** Start the progress polling loop (called on PLAYING). */
  private startPolling(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  /** Stop the progress polling loop. */
  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** Read current position + buffered fraction, fire timeUpdate callback. */
  private poll(): void {
    if (this.destroyed || !this.player) return;
    this._currentTime = this.player.getCurrentTime() || 0;
    this.callbacks.onTimeUpdate?.(this._currentTime);

    const frac = this.player.getVideoLoadedFraction();
    if (frac !== this._bufferedFraction) {
      this._bufferedFraction = frac;
      this.callbacks.onBufferedFraction?.(frac);
    }
  }

  /* ---- PlayerMediaLike implementation ---- */

  get currentTime(): number {
    if (this.player) return this.player.getCurrentTime() || 0;
    return this._currentTime;
  }

  set currentTime(sec: number) {
    if (!this.player || !this.ready) return;
    this.seekingFlag = true;
    this.player.seekTo(sec, true);
    this._currentTime = sec;
    // Clear seeking flag after the seek settles (next state change or timeout).
    setTimeout(() => {
      this.seekingFlag = false;
    }, 300);
  }

  get duration(): number {
    if (this.player && this.ready) {
      const d = this.player.getDuration();
      if (d > 0) return d;
    }
    return this._duration;
  }

  /* ---- Extended API (used by YouTubePlayer) ---- */

  /** Whether the video is currently playing. */
  get isPlaying(): boolean {
    return !this._paused && !this._ended;
  }

  /** Whether the video has ended. */
  get isEnded(): boolean {
    return this._ended;
  }

  /** Whether the video is currently paused. */
  get isPaused(): boolean {
    return this._paused;
  }

  /** Whether a seek is in progress. */
  get seeking(): boolean {
    return this.seekingFlag;
  }

  /** Whether the player is muted. */
  get muted(): boolean {
    return this._muted;
  }

  /** Buffered fraction (0-1) — YouTube exposes this as a single fraction. */
  get bufferedFraction(): number {
    return this._bufferedFraction;
  }

  /** Whether the YT.Player is ready. */
  get isReady(): boolean {
    return this.ready;
  }

  /** Play the video (delegates to YT API). */
  playVideo(): void {
    this.player?.playVideo();
  }

  /** Pause the video (delegates to YT API). */
  pauseVideo(): void {
    this.player?.pauseVideo();
  }

  /** Mute the video. */
  mute(): void {
    this._muted = true;
    this.player?.mute();
  }

  /** Unmute the video. */
  unMute(): void {
    this._muted = false;
    this.player?.unMute();
  }

  /** Set volume 0-100. */
  setVolume(vol: number): void {
    this.player?.setVolume(vol);
  }

  /** Get volume 0-100. */
  getVolume(): number {
    return this.player?.getVolume() ?? 100;
  }

  /** Destroy the backend: stop polling, destroy YT player. */
  destroy(): void {
    this.destroyed = true;
    this.stopPolling();
    try {
      this.player?.destroy();
    } catch {
      /* YT player destroy can throw if already destroyed */
    }
    this.player = null;
  }
}
